import React from "react";
import { CloseIcon } from "icons/close/CloseIcon";
import { FailedAgreementIcon } from "icons/action/FailedIcon";
import Button from "components/form/Button";
import { useTid } from "utils/testid";
import { customerFacingPaymentError } from "utils/HelperFunctions";

interface FailedPaymentProps {
  failedReason: string;
  /** False when the plan update failed before any charge was attempted. */
  isDeclined?: boolean;
  onClose: () => void;
  onRetry: () => void;
  onNavigateDashboard: () => void;
}

const FailedPayment: React.FC<FailedPaymentProps> = ({
  failedReason,
  isDeclined = true,
  onClose,
  onRetry,
  onNavigateDashboard,
}) => {
  const tid = useTid();

  return (
    <div
      className="lg:px-0 px-5 w-full h-full"
      data-testid={tid("update-plan-failed")}
      aria-label="Payment plan update failed"
    >
      <div className="flex justify-end">
        <div
          onClick={() => onClose()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClose();
            }
          }}
          className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary"
          role="button"
          tabIndex={0}
          data-testid={tid("update-plan-failed-close-button")}
          aria-label="Close failed payment plan update"
        >
          <CloseIcon size={20} aria-hidden="true" />
        </div>
      </div>
      <div className="flex flex-col justify-center items-center gap-6 h-full">
        <div className="w-full flex flex-col justify-center items-center gap-10">
          <FailedAgreementIcon aria-hidden="true" />
          <div
            className="w-full text-center lg:text-2xl text-xl text-txtPrimary font-medium"
            data-testid={tid("update-plan-failed-title")}
          >
            Payment plan update failed
          </div>
          <div className="w-full text-center">
            <p
              className="w-full px-2 py-5 bg-bgThird lg:text-xl text-lg text-txtParagraph rounded-md"
              data-testid={tid("update-plan-failed-reason")}
            >
              {customerFacingPaymentError(failedReason) || "Payment Failed"}
            </p>
          </div>
          <div className="w-full text-center text-txtParagraph lg:text-xl text-base">
            {isDeclined
              ? "Sorry! Your payment method was declined."
              : "Your plan was not changed. Please try again, or contact us if this keeps happening."}
          </div>
          <div>
            <Button
              text={isDeclined ? "Update payment method" : "Try again"}
              className="text-base px-11"
              onClick={onRetry}
              data-testid={tid("update-plan-failed-retry-button")}
              aria-label={isDeclined ? "Update payment method" : "Try again"}
            />
          </div>
        </div>
        <div className="w-full lg:text-base text-sm text-center text-btnPrimaryActive hover:!text-btnPrimaryHover ">
          <div
            className="cursor-pointer"
            onClick={onNavigateDashboard}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onNavigateDashboard();
              }
            }}
            role="button"
            tabIndex={0}
            data-testid={tid("update-plan-failed-dashboard-link")}
            aria-label="Back to dashboard"
          >
            Back to dashboard
          </div>
        </div>
      </div>
    </div>
  );
};

export default FailedPayment;
